import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin, { type DateClickArg } from '@fullcalendar/interaction'
import type { DayCellContentArg, EventClickArg, EventContentArg, EventDropArg, MoreLinkArg, MoreLinkMountArg } from '@fullcalendar/core'
import { PlusIcon } from '@/components/icons'
import type { ShownEvent } from '@/hooks/useEvents'
import { usePeople } from '@/hooks/usePeople'
import { useTaskActions } from '@/hooks/useTaskActions'
import { dateOf, endOfMonth, endOfWeek, fmtDate, startOfMonth, startOfWeek } from '@/logic/dates'
import { useCategories } from '@/store/categories'
import type { DateStr, Task } from '@/types'
import { usePrefs } from '@/store/prefs'
import { GridEntryContent } from './GridEntry'
import { addFor, calendarEntries, categoryColor, dropOnDay, openDay, openEntry } from './shared'

const renderEntry = (arg: EventContentArg) => <GridEntryContent arg={arg} />

/** The day's own buttons: its number opens Day view, and + adds a task for it. */
function dayCell(arg: DayCellContentArg) {
  const d = dateOf(arg.date)
  return (
    <span className="fcx-daycell">
      <button type="button" className="fcx-daycell__n" aria-label={`Open ${fmtDate(d)}${arg.isToday ? ', today' : ''}`} onClick={() => openDay(d)}>
        {arg.date.getDate()}
      </button>
      <button type="button" className="fcx-daycell__add" aria-label={`Add a task for ${fmtDate(d)}`} onClick={() => addFor(d)}>
        <PlusIcon size={14} weight={2.4} />
      </button>
    </span>
  )
}

/** Empty space in a day opens it in Day view; its own buttons do their own thing. */
function onDateClick(info: DateClickArg) {
  if ((info.jsEvent.target as Element | null)?.closest?.('.fcx-daycell button')) return
  openDay(dateOf(info.date))
}

/** "+N more" opens that day in Day view rather than a popover; the grid itself stays put. */
function onMoreLink(info: MoreLinkArg) {
  openDay(dateOf(info.date))
  return info.view.type
}

/**
 * FullCalendar draws "+N more" as a link with no address, announcing a popover
 * it never opens here: it is a button that opens the day.
 */
function moreLinkMounted({ el }: MoreLinkMountArg) {
  el.setAttribute('role', 'button')
  el.removeAttribute('aria-expanded')
  el.removeAttribute('aria-controls')
}

interface MonthWideProps {
  tasks: Task[]
  events: ShownEvent[]
  today: DateStr
  date: DateStr
}

/**
 * Month on a wide screen: FullCalendar's day grid, up to four entries a day
 * then "+N more". Past days keep what was done, struck through, and anything
 * left undone where it was planned. Events are filled in their owner's colour;
 * tasks are outlined with their ring and drag to another day.
 */
export function MonthWide({ tasks, events, today, date }: MonthWideProps) {
  const ref = useRef<FullCalendar>(null)
  const navigate = useNavigate()
  const actions = useTaskActions()
  const people = usePeople()
  const byCategory = useCategories((s) => s.byId)
  const month = startOfMonth(date)
  const from = startOfWeek(month)
  const to = endOfWeek(endOfMonth(month))
  const hideDone = usePrefs((s) => !!s.filter.hideDone)
  const entries = useMemo(() => calendarEntries(tasks, events, from, to, today, categoryColor(byCategory), people, hideDone), [tasks, events, from, to, today, byCategory, people, hideDone])

  useEffect(() => {
    const api = ref.current?.getApi()
    if (api && startOfMonth(dateOf(api.getDate())) !== month) api.gotoDate(month)
  }, [month])

  const onDrop = (info: EventDropArg) => dropOnDay(info, (task, day) => actions.planOn(task, day))
  const onClick = (info: EventClickArg) => openEntry(info, (id, readOnly) => navigate(`/task/${id}`, { state: readOnly ? { readOnly: true } : undefined }))
  const dayClasses = useCallback((arg: DayCellContentArg) => (dateOf(arg.date) === date ? ['fcx-day--on'] : []), [date])

  return (
    <div className="fcx fcx--month">
      <FullCalendar
        ref={ref}
        plugins={[dayGridPlugin, interactionPlugin]}
        initialView="dayGridMonth"
        initialDate={month}
        firstDay={1}
        headerToolbar={false}
        fixedWeekCount={false}
        dayMaxEvents={4}
        moreLinkClick={onMoreLink}
        moreLinkDidMount={moreLinkMounted}
        dayHeaderFormat={{ weekday: 'short' }}
        dayCellContent={dayCell}
        dayCellClassNames={dayClasses}
        eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
        height="auto"
        editable
        eventStartEditable
        eventDurationEditable={false}
        selectable={false}
        dragRevertDuration={0}
        events={entries}
        eventContent={renderEntry}
        eventDrop={onDrop}
        eventClick={onClick}
        dateClick={onDateClick}
        eventOrder="start,-duration,rank,title"
      />
    </div>
  )
}
